import { useId, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../../../../Utils/api';
import useAsync from '../../../../Hooks/useAsync';
import Card from '../../../../Components/Dashboard/Card';
import Pill from '../../../../Components/Dashboard/Pill';
import { CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { downloadBlobResponse, getBackupDownloadErrorMessage, MIN_BACKUP_FILE_SIZE } from '../../../../Utils/backupDownload';
import { formatDayMonth, formatTime } from '../../../../Utils/dates/formatDate';
import BackupRestoreGuide from '../BackupRestoreGuide';
import styles from '../adminCards.module.css';

const TITLE = 'Backup status';
const mb = bytes => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const at = iso => `${formatDayMonth(iso)}, ${formatTime(iso)}`;

// Shows only what the server recorded. With automation unset the card carries no date, age or size.
const BackupStatusCard = () => {
    const { status, data, error, reload } = useAsync(() => API.getBackupStatus().then(res => res.data), []);
    const [downloading, setDownloading] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const noticeId = useId();

    const download = async () => {
        setDownloading(true);
        try {
            const response = await API.downloadBackup();
            if (!response.data || response.data.size < MIN_BACKUP_FILE_SIZE) {
                toast.error('Backup file appears empty. Please try again or check the API logs.');
                return;
            }
            downloadBlobResponse(response, `hydromastery-backup-${new Date().toISOString().slice(0, 10)}.zip`);
            toast.success('Database backup downloaded.');
        } catch (e) {
            toast.error(await getBackupDownloadErrorMessage(e));
        } finally {
            setDownloading(false);
        }
    };

    let body;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (data) {
        const { automated, manualExport } = data;
        body = (
            <>
                <div className={styles.statusRow}>
                    <span>Automated backups</span>
                    <span>{automated.configured ? 'Configured' : 'Not configured'}</span>
                </div>
                {automated.configured && automated.schedule && (
                    <div className={styles.statusRow}><span>Schedule</span><span>{automated.schedule}</span></div>
                )}
                <div className={styles.statusRow}>
                    <span>Last automated backup</span>
                    <span>
                        {automated.lastSuccess
                            ? `Last automated backup ${at(automated.lastSuccess.finishedAt)} · ${mb(automated.lastSuccess.sizeBytes)}`
                            : 'Not available'}
                    </span>
                </div>
                {automated.overdue && <div className={styles.statusRow}><span>Status</span><Pill tone="warn">Overdue</Pill></div>}
                {automated.lastFailure && (
                    <div className={styles.statusRow}>
                        <span>{`Last failed attempt ${at(automated.lastFailure.finishedAt)} · ${automated.lastFailure.reason}`}</span>
                    </div>
                )}
                <div className={styles.statusRow}>
                    <span>Manual export</span>
                    <span>{manualExport.available ? 'Available' : 'Unavailable'}</span>
                </div>
                <div className={styles.actions}>
                    <button type="button" className={styles.btn} onClick={download} disabled={downloading}>
                        {downloading ? 'Preparing backup…' : 'Download backup'}
                    </button>
                    <button
                        type="button"
                        className={styles.btn}
                        aria-expanded={showNotice}
                        aria-controls={noticeId}
                        onClick={() => setShowNotice(v => !v)}
                    >
                        Configure automation
                    </button>
                </div>
                {showNotice && (
                    <div id={noticeId} className={styles.notice}>
                        <p style={{ margin: 0 }}>
                            Automated backups are set up by the team on the server; nothing can be changed here.
                        </p>
                    </div>
                )}
                <BackupRestoreGuide />
            </>
        );
    }

    return (
        <Card id="bk" module="admin-backup" title={TITLE} busy={status === 'loading'}>
            {body}
        </Card>
    );
};

export default BackupStatusCard;
